import {getAdminToken} from '../../auth/_requests'

const API_URL = process.env.REACT_APP_API_URL

function getAuthHeaders(): HeadersInit {
  const token = getAdminToken()
  return token ? {Authorization: `Bearer ${token}`} : {}
}

export interface SalesRecording {
  _id: string
  title: string
  description: string
  file_url: string
  file_key: string
  file_size: number
  duration: number
  original_filename: string
  tags: string[]
  salesperson: string
  rating: number | null
  is_published: boolean
  created_at: string
  updated_at: string
  transcript: string
  ai_summary: string
  ai_key_points: string[]
  ai_status: 'none' | 'processing' | 'completed' | 'error'
  ai_error: string
  ai_sale_made: boolean | null
  ai_deal_details: string
  ai_analyzed_at: string | null
}

export interface RecordingsResponse {
  recordings: SalesRecording[]
  total: number
  page: number
  totalPages: number
}

export interface StatsResponse {
  total: number
  published: number
  drafts: number
  avgRating: number
  tagCounts: {_id: string; count: number}[]
}

export async function getRecordings(params: Record<string, any> = {}): Promise<RecordingsResponse> {
  const searchParams = new URLSearchParams()
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      searchParams.append(key, String(value))
    }
  })

  const response = await fetch(`${API_URL}/admin/sales-library?${searchParams}`, {
    credentials: 'include',
    headers: getAuthHeaders(),
  })
  if (!response.ok) throw new Error('Failed to fetch recordings')
  return response.json()
}

export async function getRecording(id: string): Promise<SalesRecording> {
  const response = await fetch(`${API_URL}/admin/sales-library/${id}`, {
    credentials: 'include',
    headers: getAuthHeaders(),
  })
  if (!response.ok) throw new Error('Failed to fetch recording')
  return response.json()
}

export async function createRecording(formData: FormData): Promise<SalesRecording> {
  const token = getAdminToken()
  const headers: HeadersInit = {}
  if (token) headers.Authorization = `Bearer ${token}`

  const response = await fetch(`${API_URL}/admin/sales-library`, {
    method: 'POST',
    credentials: 'include',
    headers,
    body: formData,
  })
  if (!response.ok) throw new Error('Failed to create recording')
  return response.json()
}

export async function updateRecording(
  id: string,
  data: Partial<Pick<SalesRecording, 'title' | 'description' | 'tags' | 'salesperson' | 'rating' | 'is_published' | 'duration'>>
): Promise<SalesRecording> {
  const token = getAdminToken()
  const response = await fetch(`${API_URL}/admin/sales-library/${id}`, {
    method: 'PUT',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? {Authorization: `Bearer ${token}`} : {}),
    },
    body: JSON.stringify(data),
  })
  if (!response.ok) throw new Error('Failed to update recording')
  return response.json()
}

export async function deleteRecording(id: string): Promise<{success: boolean}> {
  const response = await fetch(`${API_URL}/admin/sales-library/${id}`, {
    method: 'DELETE',
    credentials: 'include',
    headers: getAuthHeaders(),
  })
  if (!response.ok) throw new Error('Failed to delete recording')
  return response.json()
}

export async function getStats(): Promise<StatsResponse> {
  const response = await fetch(`${API_URL}/admin/sales-library/stats`, {
    credentials: 'include',
    headers: getAuthHeaders(),
  })
  if (!response.ok) throw new Error('Failed to fetch stats')
  return response.json()
}

export async function analyzeRecording(id: string): Promise<{success: boolean; message: string}> {
  const response = await fetch(`${API_URL}/admin/sales-library/${id}/analyze`, {
    method: 'POST',
    credentials: 'include',
    headers: getAuthHeaders(),
  })
  if (!response.ok) throw new Error('Failed to start analysis')
  return response.json()
}
